"use client";

import { c as _c } from "react/compiler-runtime";
import useMergedRef from "@react-hook/merged-ref";
import { motion } from "motion/react";
import { tv } from "tailwind-variants";
import { Drawer as SheetPrimitive } from "vaul";
import { useCallbackRef } from "../../hooks";
import { ArrowBack } from "../../icons/material-symbols/outlined/ArrowBack";
import { Close } from "../../icons/material-symbols/outlined/Close";
import { overlayVariants } from "../../styles/utils/overlay";
import { customScrollbarClassname } from "../../styles/utils/scrollbar";
import { classNames } from "../../utils";
import { AnimateHeight } from "../AnimateHeight";
import { Block } from "../Block";
import { Button } from "../Button";
import { FlexCenter } from "../FlexCenter";
import { FlexColumn } from "../FlexColumn";
import { TextHeading } from "../Text";

// z-sheet index
const nestedClassName = classNames("z-[calc(var(--z-sheet)+1)]");
const sheetVariants = tv({
  base: classNames("z-sheet flex max-h-dvh max-h-screen w-full flex-col bg-bg-app focus-visible:outline-hidden", "inset-x-0 bottom-0 rounded-t-xl border border-border-1"),
  variants: {
    fullScreen: {
      true: "h-full"
    },
    nested: {
      true: nestedClassName
    },
    contained: {
      true: "absolute",
      false: "fixed"
    }
  }
});
const containerClassName = classNames("mx-auto w-full max-w-[1200px]");
const BOTTOM_SHEET_CONTENT_SELECTOR = "[data-id='BottomSheetContent']";
const BOTTOM_SHEET_FOOTER_SELECTOR = "[data-id='BottomSheetFooter']";
export function BottomSheet(t0) {
  const $ = _c(60);
  let children;
  let className;
  let container;
  let content;
  let disableHeightAnimation;
  let modal;
  let nested;
  let onBack;
  let onOpenChange;
  let open;
  let overrides;
  let props;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== t0) {
    ({
      open,
      onOpenChange,
      content,
      children,
      className,
      withCloseIcon: t1,
      onBack,
      overrides,
      modal,
      dismissible: t2,
      shouldScaleBackground: t3,
      fullScreen: t4,
      container,
      repositionInputs: t5,
      disableHeightAnimation,
      nested,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = container;
    $[4] = content;
    $[5] = disableHeightAnimation;
    $[6] = modal;
    $[7] = nested;
    $[8] = onBack;
    $[9] = onOpenChange;
    $[10] = open;
    $[11] = overrides;
    $[12] = props;
    $[13] = t1;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
  } else {
    children = $[1];
    className = $[2];
    container = $[3];
    content = $[4];
    disableHeightAnimation = $[5];
    modal = $[6];
    nested = $[7];
    onBack = $[8];
    onOpenChange = $[9];
    open = $[10];
    overrides = $[11];
    props = $[12];
    t1 = $[13];
    t2 = $[14];
    t3 = $[15];
    t4 = $[16];
    t5 = $[17];
  }
  const withCloseIcon = t1 === undefined ? true : t1;
  const dismissible = t2 === undefined ? true : t2;
  const shouldScaleBackground = t3 === undefined ? false : t3;
  const fullScreen = t4 === undefined ? false : t4;
  const repositionInputs = t5 === undefined ? false : t5;
  const t6 = Boolean(container);
  let t7;
  if ($[18] !== children) {
    t7 = <SheetPrimitive.Trigger asChild={true}>{children}</SheetPrimitive.Trigger>;
    $[18] = children;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const t8 = overrides?.Overlay;
  let t9;
  if ($[20] !== nested || $[21] !== overrides?.Overlay?.className) {
    t9 = classNames(overlayVariants(), nested && nestedClassName, overrides?.Overlay?.className);
    $[20] = nested;
    $[21] = overrides?.Overlay?.className;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t8 || $[24] !== t9) {
    t10 = <SheetPrimitive.Overlay data-testid="Overlay" {...t8} className={t9} />;
    $[23] = t8;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const t11 = Boolean(container);
  let t12;
  if ($[26] !== className || $[27] !== fullScreen || $[28] !== nested || $[29] !== t11) {
    t12 = classNames(sheetVariants({
      fullScreen,
      contained: t11,
      nested
    }), className);
    $[26] = className;
    $[27] = fullScreen;
    $[28] = nested;
    $[29] = t11;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== content || $[32] !== disableHeightAnimation) {
    t13 = disableHeightAnimation ? <FlexColumn className="max-h-[calc(100vh-16px)] w-full md:max-h-[calc(100vh-64px)]">{content}</FlexColumn> : <AnimateHeight><FlexColumn className="max-h-[calc(100vh-16px)] md:max-h-[calc(100vh-64px)]">{content}</FlexColumn></AnimateHeight>;
    $[31] = content;
    $[32] = disableHeightAnimation;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== onBack || $[35] !== overrides?.BackButton?.className) {
    t14 = onBack && <Button className={classNames("absolute top-4 left-4 z-sheet lg:top-6 lg:left-6", overrides?.BackButton?.className)} icon={ArrowBack} onClick={onBack} variant="ghost" />;
    $[34] = onBack;
    $[35] = overrides?.BackButton?.className;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== overrides?.CloseButton?.className || $[38] !== withCloseIcon) {
    t15 = withCloseIcon && <SheetPrimitive.Close className={classNames("absolute top-4 right-4 z-sheet flex items-center lg:top-6 lg:right-6", overrides?.CloseButton?.className)}><Close fill="interactive-primary-styles" /></SheetPrimitive.Close>;
    $[37] = overrides?.CloseButton?.className;
    $[38] = withCloseIcon;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== props || $[41] !== t12 || $[42] !== t13 || $[43] !== t14 || $[44] !== t15) {
    t16 = <SheetPrimitive.Content className={t12} data-id="BottomSheetContent" {...props}>{t13}{t14}{t15}</SheetPrimitive.Content>;
    $[40] = props;
    $[41] = t12;
    $[42] = t13;
    $[43] = t14;
    $[44] = t15;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== t10 || $[47] !== t16) {
    t17 = <SheetPrimitive.Portal>{t10}{t16}</SheetPrimitive.Portal>;
    $[46] = t10;
    $[47] = t16;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  let t18;
  if ($[49] !== container || $[50] !== dismissible || $[51] !== modal || $[52] !== onOpenChange || $[53] !== open || $[54] !== repositionInputs || $[55] !== shouldScaleBackground || $[56] !== t17 || $[57] !== t6 || $[58] !== t7) {
    t18 = <SheetPrimitive.Root container={container} direction="bottom" disablePreventScroll={true} dismissible={dismissible} handleOnly={t6} modal={modal} noBodyStyles={true} onOpenChange={onOpenChange} open={open} repositionInputs={repositionInputs} shouldScaleBackground={shouldScaleBackground}>{t7}{t17}</SheetPrimitive.Root>;
    $[49] = container;
    $[50] = dismissible;
    $[51] = modal;
    $[52] = onOpenChange;
    $[53] = open;
    $[54] = repositionInputs;
    $[55] = shouldScaleBackground;
    $[56] = t17;
    $[57] = t6;
    $[58] = t7;
    $[59] = t18;
  } else {
    t18 = $[59];
  }
  return t18;
}
const bottomSheetHeaderVariants = tv({
  base: classNames(containerClassName, "px-4"),
  variants: {
    variant: {
      default: "pt-4 pb-6 lg:pt-6 lg:pb-4",
      heroMedia: "flex-col justify-center pt-10 pb-6"
    }
  }
});
export const BottomSheetHeader = t0 => {
  const $ = _c(10);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      variant: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const variant = t1 === undefined ? "default" : t1;
  let t2;
  if ($[4] !== className || $[5] !== variant) {
    t2 = classNames(bottomSheetHeaderVariants({
      variant
    }), className);
    $[4] = className;
    $[5] = variant;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== props || $[8] !== t2) {
    t3 = <FlexCenter className={t2} {...props} />;
    $[7] = props;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
};
export function BottomSheetHeaderTitle(t0) {
  const $ = _c(10);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== children) {
    t1 = <TextHeading asChild={true} size="sm"><h4>{children}</h4></TextHeading>;
    $[4] = children;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== className || $[7] !== props || $[8] !== t1) {
    t2 = <SheetPrimitive.Title asChild={true} className={className} {...props}>{t1}</SheetPrimitive.Title>;
    $[6] = className;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}
export function BottomSheetBody(t0) {
  const $ = _c(13);
  let children;
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      children,
      className,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = ref;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    ref = $[4];
  }
  const [bodyRef, setBodyRef] = useCallbackRef();
  const mergedRef = useMergedRef(ref, setBodyRef);
  const hasFooter = Boolean(bodyRef?.current?.closest(BOTTOM_SHEET_CONTENT_SELECTOR)?.querySelector(BOTTOM_SHEET_FOOTER_SELECTOR));
  const t1 = hasFooter ? "pb-4" : "pb-10";
  let t2;
  if ($[5] !== className || $[6] !== t1) {
    t2 = classNames(containerClassName, "flex flex-col overflow-y-auto px-4", t1, customScrollbarClassname, className);
    $[5] = className;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== children || $[9] !== mergedRef || $[10] !== props || $[11] !== t2) {
    t3 = <Block className={t2} ref={mergedRef} {...props}>{children}</Block>;
    $[8] = children;
    $[9] = mergedRef;
    $[10] = props;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
}
export function BottomSheetFooter(t0) {
  const $ = _c(13);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      type: "spring",
      bounce: 0,
      duration: 0.3
    };
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== className) {
    t2 = classNames(containerClassName, "flex w-full flex-col gap-3 overflow-hidden p-4 pb-[calc(var(--ios-inset-bottom)+theme(spacing.4))] lg:py-6", className);
    $[5] = className;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== children || $[8] !== t2) {
    t3 = <motion.div className={t2}>{children}</motion.div>;
    $[7] = children;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== props || $[11] !== t3) {
    t4 = <motion.div className="border-border-1 border-t bg-bg-additional-1" data-id="BottomSheetFooter" layout="position" transition={t1} {...props}>{t3}</motion.div>;
    $[10] = props;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
export function BottomSheetFooterButton(t0) {
  const $ = _c(8);
  let className;
  let rest;
  if ($[0] !== t0) {
    ({
      className,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = rest;
  } else {
    className = $[1];
    rest = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = classNames("w-full", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== rest || $[6] !== t1) {
    t2 = <Button className={t1} {...rest} />;
    $[5] = rest;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export function BottomSheetDescription(t0) {
  const $ = _c(8);
  let className;
  let content;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      content,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = content;
    $[3] = props;
  } else {
    className = $[1];
    content = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className || $[5] !== content || $[6] !== props) {
    t1 = <SheetPrimitive.Description className={className} {...props}>{content}</SheetPrimitive.Description>;
    $[4] = className;
    $[5] = content;
    $[6] = props;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}

/**
 * Displays content that complements the main content of the screen.
 * The bottom variant of this is a common pattern for mobile devices.
 */